import React, {forwardRef, useEffect, useRef, useState} from 'react';
import {batch} from 'react-redux';
import {FmlxTextBox} from 'fmlx-common-ui';
import PropTypes from 'prop-types';

import styled from 'styled-components';
import {checkContainIllegalChars} from '../../lib/stringHelper';
import {TextBoxMode} from '../../constants/UIComponentEnum';
import {convertObjectToStringCSS} from '../../lib/objectHelper';
import {mergeRefs} from '../../lib/refHelper';
import IncrementDecrementArrow from './IncrementDecrementArrow';

import './FastTextBox.scss';

// a modified component of FmlxTextBox with local state value and a few added props
const TextBoxContainer = styled.div`
  ${(props) => props?.fontSize && `font-size: ${props?.fontSize} !important`}

  ${(props) =>
    props?.hidesArrowButton &&
    `
    input[type='number']::-webkit-outer-spin-button,
    input[type='number']::-webkit-inner-spin-button,
    input[type='number'] {
      -webkit-appearance: none;
      margin: 0;
      -moz-appearance: textfield !important;
    }

    input[type=number]{
      -moz-appearance: textfield;
    }
  `}


  .fmlx-text-field {
    .MuiTextField-root {
      .MuiInputBase-root {
        font-size: inherit !important;
        .MuiInputBase-input {
          font-size: inherit !important;
          min-height: 25px;
          ${(props) => props?.textBoxStyle}
        }
      }
    }
  }

  #fmlx-textbox {
    ${(props) => props?.fontSize && `font-size: ${props?.fontSize} !important`}
  }
`;

const FastTextBox = forwardRef(
  (
    {
      fontSize,
      className,
      inputStyle,
      style,
      mode,
      skipIllegalCharacterCheck,
      hidesArrowButton,
      preventScroll,
      useOutsideArrowButton,
      increment,
      precision,
      onBlur,
      onChange,
      onOverValue,
      value,
      allowDecimal,
      ...props
    },
    ref,
  ) => {
    const [localValue, setLocalValue] = useState(value);
    const [cssStyling, setCssStyling] = useState('');
    const [cursorPosition, setCursorPosiition] = useState(0);

    const isNumericTextBox = mode === TextBoxMode.NUMBER;

    const textBoxRef = useRef(null);

    const processNumericValue = (newVal) => {
      const {value: newInputVal, maxValue, minValue} = newVal;

      let valueChange = Number(newInputVal);
      if (!allowDecimal) {
        valueChange = Math.trunc(valueChange);
      }

      if (valueChange > maxValue) {
        valueChange = maxValue;
      } else if (valueChange < minValue) {
        valueChange = minValue;
      }

      return valueChange;
    };

    const onChangeHandler = (newValObj) => {
      // CornerCase when use arrow button to increase/decrease value
      // then 0 is passed as number which breaks on checkContainIllegalChars function
      let newValue = newValObj.value.toString();

      // prevent multiple zeros to be inputted
      const isMultipleZerosBehindComma = newValue.startsWith('00');
      const isContainIllegalCharacters = checkContainIllegalChars(newValue);

      if (isMultipleZerosBehindComma) {
        batch(() => {
          /**
           * HACK:
           * the value needs to be directly changed from DOM because the textbox assumes the props value (even when converted to number)
           * is the same its local value so the shown value won't changed. will still be "00xx"
           */
          textBoxRef.current.value = Number(newValue);
          setCursorPosiition(2);
        });
        return;
      }

      if (isContainIllegalCharacters && !skipIllegalCharacterCheck) {
        if (newValue !== '') setCursorPosiition(textBoxRef.current.selectionStart);
        return;
      }

      if (isNumericTextBox && newValue !== '') {
        newValue = processNumericValue(newValObj);
      }

      setLocalValue(newValObj.value.toString());
      if (onChange) onChange(newValue);
    };

    const onBlurHandler = (newValObj) => {
      if (!onBlur) return;

      onOverValue(newValObj.value > newValObj.maxValue || newValObj.value < newValObj.minValue);
      let newValue = newValObj?.value;

      if (isNumericTextBox) {
        newValue = processNumericValue(newValObj);

        if (newValObj?.value !== newValue) {
          setLocalValue(newValue);
        }
      }

      onBlur(newValue);
    };

    const onEnterKeyHandler = () => {
      textBoxRef.current.blur();
    };

    useEffect(() => {
      if (!inputStyle) return;

      const cssString = convertObjectToStringCSS(inputStyle, true);
      setCssStyling(cssString);
    }, [inputStyle]);

    // keeps the blinking cursor in place if the input contains illegal characters
    useEffect(() => {
      if (cursorPosition) {
        const originalInputType = textBoxRef.current.type;

        textBoxRef.current.type = 'text';
        textBoxRef.current.setSelectionRange(cursorPosition - 1, cursorPosition - 1);
        textBoxRef.current.type = originalInputType;
        setCursorPosiition(0);
      }
    }, [cursorPosition]);

    useEffect(() => {
      if (!textBoxRef.current || !preventScroll) return null;

      const preventDefault = (e) => e.preventDefault();
      const textBoxElement = textBoxRef.current;

      textBoxElement.addEventListener('wheel', preventDefault);
      return () => textBoxElement.removeEventListener('wheel', preventDefault);
    }, [preventScroll]);

    useEffect(() => {
      setLocalValue(value);
    }, [value]);

    const handlePreventSubmit = (e) => {
      e.preventDefault();
    };

    return (
      <TextBoxContainer
        style={style}
        hidesArrowButton={hidesArrowButton}
        textBoxStyle={cssStyling}
        fontSize={fontSize}
        className={`override-bootstrap-iso fast-text-box ${className} ${!useOutsideArrowButton ? 'hideArrow' : 'showArrow'}`}
      >
        {/* - To disabled the default popup message that provide by HTML
          - To handle for all component that using FastTextBox */}
        <form noValidate onSubmit={handlePreventSubmit}>
          <FmlxTextBox
            ref={mergeRefs(ref, textBoxRef)}
            value={localValue}
            onChange={onChangeHandler}
            onBlur={onBlurHandler}
            onEnterKeyPressed={onEnterKeyHandler}
            onClick={(e) => e.stopPropagation()}
            mode={mode}
            precision={precision}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
          />
        </form>
        {useOutsideArrowButton && (
          <IncrementDecrementArrow
            value={localValue}
            increment={increment}
            precision={precision}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
            onChange={(val) => {
              if (onChange) {
                onChangeHandler({value: val});
              } else {
                onBlurHandler({value: val});
              }
            }}
          />
        )}
      </TextBoxContainer>
    );
  },
);

FastTextBox.propTypes = {
  value: PropTypes.any,
  fontSize: PropTypes.any,
  className: PropTypes.string,
  style: PropTypes.object,
  inputStyle: PropTypes.object,
  hidesArrowButton: PropTypes.bool,
  useOutsideArrowButton: PropTypes.bool,
  skipIllegalCharacterCheck: PropTypes.bool,
  mode: PropTypes.string,
  onChange: PropTypes.func,
  onOverValue: PropTypes.func,
  onBlur: PropTypes.func,
  preventScroll: PropTypes.bool,
  increment: PropTypes.number,
  precision: PropTypes.number,
  allowDecimal: PropTypes.bool,
};

FastTextBox.defaultProps = {
  value: '',
  className: '',
  mode: 'text',
  inputStyle: null,
  fontSize: null,
  hidesArrowButton: false,
  useOutsideArrowButton: false,
  skipIllegalCharacterCheck: false,
  style: {},
  onChange: () => {},
  onOverValue: () => {},
  onBlur: null,
  preventScroll: false,
  increment: 1,
  precision: 1,
  allowDecimal: true,
};

export default FastTextBox;
